import { CSSProperties, ReactElement } from 'react';

import { rgba } from 'polished';
import styled from 'styled-components';

import { IconProps, IconSmallProps } from '@components/icons';
import { IconCloseSmall } from '@components/icons/close-small';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export const TAG_VARIANTS = [
  'orange',
  'purple',
  'green',
  'red',
  'blue',
  'empty',
  'indigo',
  'teal',
  'gray',
  'indigo-trans-bg',
  'customColor',
] as const;
export const TAG_SIZES = ['small', 'large'] as const;
export const TAG_HEIGHT_SMALL = 16;
export const TAG_HEIGHT_LARGE = 24;
export interface TagProps {
  size: (typeof TAG_SIZES)[number];
  variant: (typeof TAG_VARIANTS)[number];
  color?: string;
  onRemove?(): void;
  onClick?(): void;
  text: string;
  icon?: ReactElement<IconProps | IconSmallProps>;
  styles?: CSSProperties;
}

export function Tag(props: TagProps) {
  const { size, variant, text = null, icon, onRemove, color, onClick, styles, ...rest } = props;
  return (
    <TagContainer
      data-variant={variant}
      data-removable={Boolean(onRemove)}
      data-size={size}
      color={color}
      onClick={onRemove ?? onClick}
      style={styles}
      {...rest}
    >
      {icon}
      {text}
      {onRemove && (
        <RemoveIconContainer data-size={size} data-variant={variant}>
          <IconCloseSmall />
        </RemoveIconContainer>
      )}
    </TagContainer>
  );
}

const RemoveIconContainer = styled.div({
  '&[data-size="small"]': {
    width: '0.75rem',
    height: '0.75rem',
  },

  '&[data-size="large"]': {
    width: '1rem',
    height: '1rem',
    marginLeft: '0.25rem',
  },
});

const TagContainer = styled.div((p) => ({
  svg: {
    marginRight: pxToRem(4),
  },
  '--tag-height': pxToRem(TAG_HEIGHT_SMALL),
  maxHeight: 'var(--tag-height)',
  minHeight: 'var(--tag-height)',

  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'transparent',
  display: 'inline-flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.25rem',
  transition: 'background 150ms ease-out',
  whiteSpace: 'nowrap',

  '&[data-size="small"]': {
    padding: '0 0.25rem',
    font: theme.font.semibold10,
  },

  '&[data-size="large"]': {
    '--tag-height': pxToRem(TAG_HEIGHT_LARGE),
    padding: '0 0.5rem',
    font: theme.font.semibold12,
  },

  '&[data-variant="teal"]': {
    borderColor: theme.color.elementTeal100,
    color: theme.color.elementTeal200,
    backgroundColor: theme.color.tealTrans10,

    '&[data-removable="true"]:hover': {
      backgroundColor: theme.color.teal20,
    },
  },

  '&[data-variant="orange"]': {
    borderColor: theme.color.elementOrange100,
    color: theme.color.elementOrange200,
    backgroundColor: theme.color.orangeTrans10,

    '&[data-removable="true"]:hover': {
      borderColor: theme.color.orange200,
      backgroundColor: theme.color.orange20,
    },
  },

  '&[data-variant="purple"]': {
    borderColor: theme.color.border20,
    color: theme.color.textPrimary,
    backgroundColor: theme.color.uiTrans10,

    '&[data-removable="true"]:hover': {
      backgroundColor: theme.color.uiTrans20,
    },
  },

  '&[data-variant="green"]': {
    borderColor: theme.color.elementGreen100,
    color: theme.color.elementGreen200,
    backgroundColor: theme.color.greenTrans10,

    '&[data-removable="true"]:hover': {
      borderColor: theme.color.green200,
      backgroundColor: theme.color.green20,
    },
  },

  '&[data-variant="red"]': {
    borderColor: theme.color.elementRed100,
    color: theme.color.elementRed100,
    backgroundColor: theme.color.redTrans10,

    '&[data-removable="true"]:hover': {
      borderColor: theme.color.elementRed200,
      color: theme.color.elementRed200,
      backgroundColor: theme.color.red20,
    },
  },

  '&[data-variant="blue"]': {
    borderColor: theme.color.elementBlue100,
    color: theme.color.elementBlue100,
    backgroundColor: theme.color.blueTrans10,

    '&[data-removable="true"]:hover': {
      color: theme.color.elementBlue200,
      borderColor: theme.color.elementBlue200,
      backgroundColor: theme.color.blue20,
    },
  },

  '&[data-variant="indigo"]': {
    borderColor: theme.color.elementIndigo100,
    color: theme.color.elementIndigo100,
    backgroundColor: theme.color.indigoTrans10,

    '&[data-removable="true"]:hover': {
      borderColor: theme.color.indigo200,
      color: theme.color.indigo200,
      backgroundColor: theme.color.indigo20,
    },
  },
  // delete this variant, when the tag variants are updated with the correct colors
  '&[data-variant="indigo-trans-bg"]': {
    borderColor: theme.color.elementIndigo100,
    color: theme.color.elementIndigo100,
    backgroundColor: theme.color.indigoTrans5,

    '&[data-removable="true"]:hover': {
      borderColor: theme.color.indigo200,
      color: theme.color.indigo200,
      backgroundColor: theme.color.indigo20,
    },
  },

  '&[data-variant="gray"]': {
    color: theme.color.textHelper,
    backgroundColor: theme.color.uiTrans5,

    '&[data-removable="true"]:hover': {
      color: theme.color.textHelper,
      backgroundColor: theme.color.uiTrans10,
    },
  },

  '&[data-variant="empty"]': {
    color: theme.color.textHelper,
    backgroundColor: theme.color.uiTrans5,

    '&[data-removable="true"]:hover': {
      backgroundColor: theme.color.uiTrans10,
    },
  },

  '&[data-variant="customColor"]': {
    ...(p.color && {
      borderColor: p.color,
      color: p.color,
      backgroundColor: rgba(p.color, 0.1),
    }),
  },

  '&[data-removable="true"]': {
    paddingRight: '0.125rem',

    '&:hover': {
      cursor: 'pointer',
    },
  },
}));
